import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, of, throwError } from 'rxjs';
import { ApiService } from '@/core/services/api/api.service';
import { AgenceService } from './agence.service';
import {
  ParametresProfil,
  ServiceItem,
  ParametresCommune,
  SavePermissionsTypeCreditDto,
} from '../interfaces/referentiel.interface';

/** Référentiels transverses (profils, services, communes, quartiers) et permissions type crédit par utilisateur. */
@Injectable({ providedIn: 'root' })
export class ReferentielService {
  private readonly api = inject(ApiService);
  private readonly agenceService = inject(AgenceService);

  getProfils() {
    return this.api.get<{ data: ParametresProfil[] }>('/profils').pipe(
      map((res) => res.data),
      catchError((err) => throwError(() => err)),
    );
  }

  getServices() {
    return this.api.get<{ data: ServiceItem[] }>('/services').pipe(
      map((res) => res.data),
      catchError((err) => throwError(() => err)),
    );
  }

  getCommunes() {
    return this.api.get<{ communes: ParametresCommune[] }>('/pays_commune').pipe(
      map((res) => res.communes),
      catchError((err) => throwError(() => err)),
    );
  }

  getListeQuartiers() {
    return this.api.get<{ quartiers: string[] }>('/listeQuartiers').pipe(
      map((res) => res.quartiers ?? []),
      catchError(() => of([])),
    );
  }

  getTypesCredit() {
    return this.api
      .get<{ typeCredits: { id: number; libelle: string }[] }>('/credit/listeTypeCredit')
      .pipe(
        map((res) => res.typeCredits ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  getPermissionsTypeCredit(userId: number) {
    return this.api
      .get<{ data: { typeCredit: number; type: string }[] }>(`/credit/permisionComite/${userId}`)
      .pipe(
        map((res) => res.data ?? []),
        catchError((err) => throwError(() => err)),
      );
  }

  savePermissionsTypeCredit(data: SavePermissionsTypeCreditDto) {
    return this.api
      .post('/credit/saveChargeComite', data)
      .pipe(catchError((err) => throwError(() => err)));
  }

  getFormData() {
    return forkJoin({
      profils: this.getProfils(),
      services: this.getServices(),
      agences: this.agenceService.getAgences(),
      communes: this.getCommunes(),
    }).pipe(catchError((err) => throwError(() => err)));
  }
}
